(() => {
  const STRINGS = window.backInStockStrings || {};
  const API_REVISION = "2024-07-15";

  let activeVariantId = null;
  let modal = null;
  let cachedCompanyId = null;

  function getCompanyId() {
    if (cachedCompanyId) return cachedCompanyId;

    const fromTheme =
      document.documentElement.dataset.klaviyoCompanyId ||
      window.klaviyoCompanyId ||
      "";

    if (fromTheme) {
      cachedCompanyId = fromTheme;
      return cachedCompanyId;
    }

    const scripts = document.querySelectorAll(
      'script[src*="klaviyo"], script[src*="company_id"]'
    );
    for (const script of scripts) {
      const match = script.src.match(/[?&]company_id=([^&]+)/);
      if (match) {
        cachedCompanyId = decodeURIComponent(match[1]);
        return cachedCompanyId;
      }
    }

    const klaviyoQueue = window.klaviyo || [];
    for (let i = 0; i < klaviyoQueue.length; i += 1) {
      const item = klaviyoQueue[i];
      if (Array.isArray(item) && item[0] === "account" && item[1]) {
        cachedCompanyId = item[1];
        return cachedCompanyId;
      }
    }

    if (window.klaviyo?.account) {
      cachedCompanyId = window.klaviyo.account;
      return cachedCompanyId;
    }

    return "";
  }

  function getApiUrl(endpoint) {
    return `https://a.klaviyo.com/client/${endpoint}/?company_id=${getCompanyId()}`;
  }

  function getLocaleFromPath() {
    const match = window.location.pathname.match(/^\/([a-z]{2})(?=\/|$)/i);
    return match ? match[1].toLowerCase() : "";
  }

  function getLocale() {
    const pathLocale = getLocaleFromPath();
    if (pathLocale) return pathLocale;

    const themeLocale = window.backInStockLocale?.toLowerCase();
    if (themeLocale) return themeLocale;

    const htmlLocale = document.documentElement.lang?.split("-")[0]?.toLowerCase();
    if (htmlLocale) return htmlLocale;

    return "fr";
  }

  function getKlaviyoLocaleTag() {
    const locale = getLocale();
    if (locale === "fr") return "fr-FR";
    if (locale === "en") return "en-US";
    return locale;
  }

  function identifyKlaviyoProfile(email) {
    const identifyPayload = {
      $email: email,
      $locale: getKlaviyoLocaleTag(),
    };

    if (Array.isArray(window.klaviyo)) {
      window.klaviyo.push(["identify", identifyPayload]);
    }

    if (Array.isArray(window._learnq)) {
      window._learnq.push(["identify", identifyPayload]);
    }
  }

  function getModal() {
    if (!modal) modal = document.querySelector("back-in-stock-modal");
    return modal;
  }

  function syncVariantInput(productForm, variantId) {
    if (!productForm || !variantId) return;

    const variantInput = productForm.querySelector(
      'input.product-variant-id, input[name="id"]'
    );
    if (!variantInput) return;

    variantInput.value = variantId;
    variantInput.disabled = false;
    variantInput.removeAttribute("disabled");
  }

  function buildKlaviyoVariantId(variantId) {
    return `$shopify:::$default:::${variantId}`;
  }

  function setError(message) {
    const modalEl = getModal();
    const errorEl = modalEl?.querySelector("[data-back-in-stock-error]");
    if (!errorEl) return;

    if (message) {
      errorEl.textContent = message;
      errorEl.classList.remove("hidden");
    } else {
      errorEl.textContent = "";
      errorEl.classList.add("hidden");
    }
  }

  function setLoading(isLoading) {
    const modalEl = getModal();
    const submitButton = modalEl?.querySelector(
      "[data-back-in-stock-form] button[type='submit']"
    );
    const submitText = modalEl?.querySelector("[data-back-in-stock-submit-text]");
    const loadingText = modalEl?.querySelector("[data-back-in-stock-loading-text]");

    if (!submitButton) return;

    submitButton.disabled = isLoading;
    submitButton.classList.toggle("loading", isLoading);
    submitText?.classList.toggle("hidden", isLoading);
    loadingText?.classList.toggle("hidden", !isLoading);
  }

  function showSuccessView() {
    const modalEl = getModal();
    modalEl?.querySelector("[data-back-in-stock-form-view]")?.classList.add("hidden");
    modalEl?.querySelector("[data-back-in-stock-success-view]")?.classList.remove("hidden");
  }

  function resetModalView() {
    const modalEl = getModal();
    modalEl?.querySelector("[data-back-in-stock-form-view]")?.classList.remove("hidden");
    modalEl?.querySelector("[data-back-in-stock-success-view]")?.classList.add("hidden");
    setError("");
    setLoading(false);
  }

  function openModal(variantId) {
    const modalEl = getModal();
    if (!modalEl || !variantId) return;

    const companyId = getCompanyId();
    if (!companyId) {
      console.warn(
        "Klaviyo company ID not found. Set it in Theme settings > Klaviyo, or ensure the Klaviyo app embed is enabled."
      );
      return;
    }

    activeVariantId = variantId;
    resetModalView();
    modalEl.hidden = false;
    modalEl.setAttribute("aria-hidden", "false");
    document.body.classList.add("back-in-stock-modal-open");

    modalEl.querySelector("#BackInStockEmail")?.focus();
  }

  function closeModal() {
    const modalEl = getModal();
    if (!modalEl) return;

    modalEl.hidden = true;
    modalEl.setAttribute("aria-hidden", "true");
    document.body.classList.remove("back-in-stock-modal-open");
    activeVariantId = null;
  }

  async function updateProfileLocale(email) {
    const locale = getLocale();
    const payload = {
      data: {
        type: "profile",
        attributes: {
          email,
          locale: getKlaviyoLocaleTag(),
          properties: {
            $locale: getKlaviyoLocaleTag(),
            $locale_language: locale,
            shop_language: locale,
          },
        },
      },
    };

    const response = await fetch(getApiUrl("profiles"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      let message = STRINGS.error || "An error occurred.";
      try {
        const data = await response.json();
        message = data?.errors?.[0]?.detail || message;
      } catch (_error) {
        // ignore
      }
      throw new Error(message);
    }
  }

  async function subscribe(email) {
    identifyKlaviyoProfile(email);
    await updateProfileLocale(email);

    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: { email },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: buildKlaviyoVariantId(activeVariantId),
            },
          },
        },
      },
    };

    const response = await fetch(getApiUrl("back-in-stock-subscriptions"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202) return;

    let message = STRINGS.error || "An error occurred.";
    try {
      const data = await response.json();
      message = data?.errors?.[0]?.detail || message;
    } catch (_error) {
      // 202 responses have no body
    }

    throw new Error(message);
  }

  async function handleFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const emailInput = form.querySelector('input[name="email"]');
    const email = emailInput?.value?.trim();

    if (!email || !emailInput.checkValidity()) {
      setError(STRINGS.invalidEmail || "Invalid email address.");
      return;
    }

    if (!activeVariantId) {
      setError(STRINGS.error || "An error occurred.");
      return;
    }

    if (!getCompanyId()) {
      setError(STRINGS.error || "An error occurred.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await subscribe(email);
      showSuccessView();
    } catch (error) {
      setError(error.message || STRINGS.error);
    } finally {
      setLoading(false);
    }
  }

  function onVariantUpdated({ variant } = {}) {
    if (!variant?.id) return;

    document.querySelectorAll("[data-back-in-stock]").forEach((wrapper) => {
      wrapper.dataset.variantId = variant.id;
      syncVariantInput(wrapper.closest("product-form"), variant.id);
    });
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest("[data-back-in-stock-trigger]");
    if (!trigger) return;

    event.preventDefault();

    const wrapper = trigger.closest("[data-back-in-stock]");
    const productForm = trigger.closest("product-form");
    const variantId =
      wrapper?.dataset.variantId ||
      productForm?.querySelector('input[name="id"]')?.value;

    if (variantId) syncVariantInput(productForm, variantId);
    openModal(variantId);
  }

  function handleCloseClick(event) {
    if (!event.target.closest("[data-back-in-stock-close]")) return;
    closeModal();
  }

  function handleEscape(event) {
    if (event.key !== "Escape") return;
    const modalEl = getModal();
    if (modalEl && !modalEl.hidden) closeModal();
  }

  function init() {
    const modalEl = getModal();
    if (!modalEl) return;

    document.addEventListener("click", handleTriggerClick);
    document.addEventListener("click", handleCloseClick);
    document.addEventListener("keydown", handleEscape);
    modalEl
      .querySelector("[data-back-in-stock-form]")
      ?.addEventListener("submit", handleFormSubmit);

    if (window.eventBus) {
      window.eventBus.on("variant:updated", onVariantUpdated);
      window.eventBus.on("variant:change", onVariantUpdated);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
